import { FC, memo } from "react";
import styled, { css } from "styled-components";
import { readableColor, tint } from "polished";
import { FileType, IMessage } from "../../../common/interfaces/IMessage";
import { useVideoChat } from "../../../hooks/useVideoChat";
import pdfIcon from "../../../assets/file_icons/pdf.svg";

const MessageWrapper = styled("div")<{ isLocal: boolean }>`
  display: flex;
  position: relative;
  width: 100%;
  margin-bottom: 10px;
  ${(props) =>
    props.isLocal &&
    css`
      justify-content: flex-end;
    `}
`;

const MessageContent = styled("div")<{ $arrowSize: number }>`
  display: flex;
  flex-flow: column;
  max-width: 40%;
  font-size: 14px;
  line-height: 14px;
  padding: 5px 7px;
  border-radius: 5px;
  min-width: 50px;
  &:after {
    content: "";
    height: 0;
    width: 0;
    position: absolute;
    top: 8px;
  }
`;

const RemoteMessage = styled(MessageContent)`
  background-color: #f0f0f0;
  color: #2a3142;
  &:after {
    left: -${(props) => props.$arrowSize}px;
    border-top: ${(props) => props.$arrowSize}px solid transparent;
    border-bottom: ${(props) => props.$arrowSize}px solid transparent;
    border-right: ${(props) => props.$arrowSize}px solid #f0f0f0;
  }
`;

const LocalMessage = styled(MessageContent)`
  background-color: ${(props) => props.theme.colors.chatMessage};
  color: ${(props) => readableColor(props.theme.colors.chatMessage)};
  &:after {
    right: -${(props) => props.$arrowSize}px;
    border-top: ${(props) => props.$arrowSize}px solid transparent;
    border-bottom: ${(props) => props.$arrowSize}px solid transparent;
    border-left: ${(props) => props.$arrowSize}px solid
      ${(props) => props.theme.colors.chatMessage};
  }
`;

const MessageText = styled("p")`
  word-break: break-word;
  white-space: pre-wrap;
  margin: 0;
  padding: 2px 0 4px 0;
`;

const MessageFile = styled("a")<{ isLocal: boolean }>`
  display: flex;
  flex-flow: column;
  align-items: ${(props) => (props.isLocal ? "flex-end" : "flex-start")};
  gap: 8px;
  text-decoration: underline;
  color: ${(props) =>
    props.isLocal ? readableColor(props.theme.colors.chatMessage) : "#2a3142"};
  ${(props) =>
    props.isLocal &&
    css`
      span {
        text-align: end;
      }
    `}
`;

const Image = styled("img")`
  width: 100%;
`;

const FileName = styled("span")`
  word-wrap: break-word;
  width: fit-content;
  font-size: 12px;
`;

const Timestamp = styled("span")<{ isLocal: boolean }>`
  display: flex;
  justify-content: flex-end;
  font-style: italic;
  font-size: 11px;
  color: ${(props) =>
    props.isLocal
      ? tint(0.25, readableColor(props.theme.colors.chatMessage))
      : "#949494"};
`;

interface IMessageItemProps {
  message: IMessage;
}

const Message: FC<IMessageItemProps> = ({ message }) => {
  const { permission } = useVideoChat();
  const isLocal = permission === message.sender.type;

  const handleFileType = (fileInfo: FileType) => {
    switch (fileInfo.dataType) {
      case "pdf":
        return (
          <>
            <Image src={pdfIcon} height="80" alt="PDF Icon" />
            <FileName>{fileInfo.fileName}</FileName>
          </>
        );
      default:
        return <Image src={fileInfo.thumb || fileInfo.url} alt={fileInfo.fileName} />;
    }
  };

  const handleMessageTimestamp = (dateTime: string) => (
    <Timestamp isLocal={isLocal}>
      {new Date(dateTime).toLocaleTimeString("pt-br", {
        hour: "2-digit",
        minute: "2-digit",
      })}
    </Timestamp>
  );

  const renderMessageItem = (content: JSX.Element) => (
    <MessageWrapper isLocal={isLocal}>
      {isLocal ? (
        <LocalMessage $arrowSize={6}>
          {content}
          {handleMessageTimestamp(message.createdAt)}
        </LocalMessage>
      ) : (
        <RemoteMessage $arrowSize={6}>
          {content}
          {handleMessageTimestamp(message.createdAt)}
        </RemoteMessage>
      )}
    </MessageWrapper>
  );

  const handleShowMessage = () => {
    if (!message?.content) return null;

    return (
      <>
        {message.content.files &&
          Array.isArray(message.content.files) &&
          message.content.files.map((file, index) =>
            renderMessageItem(
              <MessageFile isLocal={isLocal} href={file.url} target="_blank">
                {handleFileType(file)}
              </MessageFile>
            )
          )}
        {message.content.text &&
          renderMessageItem(<MessageText>{message.content.text}</MessageText>)}
      </>
    );
  };

  return <>{handleShowMessage()}</>;
};

export default memo(Message);
